// Shared types used across core game and adapters.

export type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error";

export interface PlayerProfile {
  id: string;
  username: string;
  isPremium?: boolean;
}

export interface SaveData {
  highestWave: number;
  totalCoins: number;
  unlocks: string[];
  noAdsPass?: boolean;
}

export const DEFAULT_SAVE: SaveData = {
  highestWave: 0,
  totalCoins: 0,
  unlocks: [],
  noAdsPass: false,
};

// ---------- Game-domain types ----------

export type Element = "fire" | "water" | "earth" | "nature";

export const ELEMENT_LIST: Element[] = ["fire", "water", "earth", "nature"];

export const ELEMENT_META: Record<Element, { label: string; color: number; emoji: string }> = {
  fire: { label: "Fire", color: 0xff5a3a, emoji: "🔥" },
  water: { label: "Water", color: 0x4cc9f0, emoji: "💧" },
  earth: { label: "Earth", color: 0xb08a4f, emoji: "🌍" },
  nature: { label: "Nature", color: 0x7be36b, emoji: "🍃" },
};

export type TowerId =
  | "fire" | "water" | "earth" | "nature"
  | "steam" | "lava" | "forest_fire" | "mud" | "ice" | "vine"
  | "brew" | "voidmage" | "beast" | "tide"
  | "necro" | "phantom" | "luna" | "tinker";

export interface TowerDef {
  id: TowerId;
  label: string;
  /** Elements required to unlock (ALL must be researched). */
  recipe: Element[];
  cost: number;
  range: number;
  /** Damage per shot. */
  damage: number;
  /** Shots per second. */
  rate: number;
  /** Tint color. */
  color: number;
  /** AOE radius — 0 means single-target. */
  splash: number;
  /** DOT damage per second over duration ms. */
  dot?: { dps: number; duration: number };
  /** Chill/slow applied on hit — scales target speed by `factor` for `duration` ms. */
  slow?: { factor: number; duration: number };
  /** Critical-hit profile — `chance` (0..1) to deal `mult`x damage. */
  crit?: { chance: number; mult: number };
  /** Description for UI. */
  description: string;
}

export type EnemyId =
  | "grunt" | "fast" | "tank" | "boss" | "elite" | "titan"
  | "wraith" | "hellhound" | "golem" | "brood"
  | "skeleton" | "demon" | "spider" | "crystal" | "vampire" | "magma_knight" | "shadow"
  | "overlord" | "warlord" | "worldender";

export interface EnemyDef {
  id: EnemyId;
  label: string;
  hp: number;
  /** Pixels per second along path. */
  speed: number;
  /** Gold awarded on kill. */
  gold: number;
  /** Lives lost if leaks past end. */
  damage: number;
  color: number;
  /** Display radius in px. */
  radius: number;
}

export interface WaveSpawn {
  enemy: EnemyId;
  count: number;
  /** ms between spawns. */
  interval: number;
  /** ms delay before first spawn from start of wave. */
  delay?: number;
}

export interface WaveDef {
  index: number;
  spawns: WaveSpawn[];
  /** Bonus gold awarded for completing the wave. */
  bonusGold: number;
}
